/**
 * パスキーの一覧・追加・名前の変更・削除（管理画面・マイページのアカウント設定、強制登録画面で共用）
 *
 * <div class="c-passkey-manager" data-base-path="/admin/account/passkeys"> の中に
 * 一覧（[data-passkey-list]）・追加ボタン（[data-passkey-add]）・エラー表示（[data-passkey-error]）・
 * 再認証の欄（[data-passkey-confirm]）・メール確認の欄（[data-passkey-email-verify]）を置く
 *
 * 追加時は名前を聞かずに登録し（既定名「パスキー」）、追加した行をそのまま名前の編集状態にする。
 * 名前は各行の「名前を変更」からいつでも変更できる（PATCH {base}/{id}、本人確認なし）
 *
 * 追加・削除の前の本人確認
 * - 403 REAUTHENTICATION_REQUIRED（パスキーが1件以上）… 再認証の欄を表示し、
 *   パスキー（{base}/reauth/options → {base}/reauth）またはパスワード（{base}/confirm-password）で確認してから元の操作をやり直す
 * - 403 EMAIL_VERIFICATION_REQUIRED（最初の1件の登録）… メール確認の欄を表示して確認コードを送り（{base}/email-code）、
 *   {base}/email-code/verify で確認してから元の操作をやり直す
 */
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { csrfToken } from '@/js/_common';

const formatDateTime = (value) => {
    if (!value) return '未使用';
    return new Intl.DateTimeFormat('ja-JP', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
};

const request = (url, method, body = null) => fetch(url, {
    method,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
        'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: body ? JSON.stringify(body) : null,
});

/** 本人確認をキャンセルしたときのエラー（メッセージは表示しない） */
const VERIFICATION_CANCELLED = 'VERIFICATION_CANCELLED';
const isVerificationCancelled = (e) => !!e && e.code === VERIFICATION_CANCELLED;

const EMAIL_CODE_MESSAGES = {
    EMAIL_CODE_INVALID: '確認コードが正しくありません',
    EMAIL_CODE_EXPIRED: '確認コードの有効期限が切れました。もう一度送信してください',
    EMAIL_CODE_TOO_MANY: '確認コードの入力回数が上限に達しました。もう一度送信してください',
    EMAIL_CODE_RESEND_WAIT: 'しばらく待ってから再送してください',
    EMAIL_CODE_ACCOUNT_LOCKED: '確認コードの送信・入力が多すぎます。時間をおいてお試しください',
    EMAIL_CODE_SEND_FAILED: '確認コードを送信できませんでした。時間をおいてお試しください',
};
// 再送できるまでの秒数（サーバー側の設定と合わせる）
const RESEND_WAIT_SECONDS = 60;

/** 本人確認が必要なレスポンスなら { error, methods } を返す */
const readVerificationRequired = async (response) => {
    if (!response || response.status !== 403) return null;
    const data = await response.clone().json().catch(() => null);
    if (!data || !['REAUTHENTICATION_REQUIRED', 'EMAIL_VERIFICATION_REQUIRED'].includes(data.error)) return null;
    return { error: data.error, methods: Array.isArray(data.methods) ? data.methods : [] };
};

const readErrorCode = async (response) => {
    const data = response ? await response.json().catch(() => null) : null;
    return data && data.error ? data.error : null;
};

const setupManager = (root) => {
    const basePath = root.dataset.basePath;
    const listArea = root.querySelector('[data-passkey-list]');
    const nameMaxLength = Number(root.dataset.nameMaxLength) || 50;
    const addButton = root.querySelector('[data-passkey-add]');
    const errorArea = root.querySelector('[data-passkey-error]');
    const addLabel = addButton ? addButton.textContent.trim() : 'パスキーを追加';
    // 再認証の欄
    const confirmArea = root.querySelector('[data-passkey-confirm]');
    const confirmInput = root.querySelector('[data-passkey-confirm-password]');
    const confirmSubmit = root.querySelector('[data-passkey-confirm-submit]');
    const confirmPasskey = root.querySelector('[data-passkey-confirm-passkey]');
    const confirmCancel = root.querySelector('[data-passkey-confirm-cancel]');
    // メール確認の欄
    const emailArea = root.querySelector('[data-passkey-email-verify]');
    const emailSentTo = root.querySelector('[data-passkey-email-sent-to]');
    const emailInput = root.querySelector('[data-passkey-email-code]');
    const emailSubmit = root.querySelector('[data-passkey-email-submit]');
    const emailResend = root.querySelector('[data-passkey-email-resend]');
    const emailCancel = root.querySelector('[data-passkey-email-cancel]');
    const resendLabel = emailResend ? emailResend.textContent.trim() : '確認コードを再送する';
    let resendTimer = null;
    let pendingVerification = null;

    const setError = (message) => {
        if (!errorArea) return;
        errorArea.textContent = message || '';
        errorArea.hidden = !message;
    };

    // ─── 本人確認（共通） ───
    const closeVerification = (verified) => {
        if (confirmArea) confirmArea.hidden = true;
        if (confirmInput) confirmInput.value = '';
        if (emailArea) emailArea.hidden = true;
        if (emailInput) emailInput.value = '';
        stopResendTimer();
        const resolve = pendingVerification;
        pendingVerification = null;
        if (resolve) resolve(verified);
    };

    /** 確認の欄を表示し、確認できたら true、キャンセルなら false を返す */
    const waitVerification = (area) => {
        if (!area) return Promise.resolve(false);
        if (pendingVerification) closeVerification(false);
        area.hidden = false;
        return new Promise((resolve) => {
            pendingVerification = resolve;
        });
    };

    // ─── 再認証（パスキー・パスワード） ───
    const askReauthentication = (methods) => {
        if (!confirmArea) return Promise.resolve(false);
        confirmArea.querySelectorAll('[data-passkey-confirm-method]').forEach((el) => {
            el.hidden = !methods.includes(el.dataset.passkeyConfirmMethod);
        });
        const promise = waitVerification(confirmArea);
        if (methods.includes('password') && confirmInput) {
            confirmInput.focus();
        } else if (confirmPasskey) {
            confirmPasskey.focus();
        }
        return promise;
    };

    const submitPassword = async () => {
        if (!pendingVerification || !confirmInput) return;
        setError(null);
        if (confirmSubmit) confirmSubmit.disabled = true;
        try {
            const response = await request(`${basePath}/confirm-password`, 'POST', {
                current_password: confirmInput.value,
            }).catch(() => null);
            if (response && response.ok) {
                closeVerification(true);
                return;
            }
            setError((await readErrorCode(response)) === 'PASSWORD_INVALID'
                ? 'パスワードが正しくありません'
                : 'パスワードを確認できませんでした');
            confirmInput.focus();
        } finally {
            if (confirmSubmit) confirmSubmit.disabled = false;
        }
    };

    const submitPasskey = async () => {
        if (!pendingVerification) return;
        setError(null);
        if (confirmPasskey) confirmPasskey.disabled = true;
        try {
            const optionsResponse = await request(`${basePath}/reauth/options`, 'POST');
            if (!optionsResponse.ok) throw new Error('REAUTH_OPTIONS_FAILED');
            const { options } = await optionsResponse.json();
            const assertion = await startAuthentication({ optionsJSON: options });
            const response = await request(`${basePath}/reauth`, 'POST', {
                assertion: JSON.stringify(assertion),
            });
            if (response.ok) {
                closeVerification(true);
                return;
            }
            setError('パスキーで確認できませんでした');
        } catch (e) {
            setError(e && e.name === 'NotAllowedError'
                ? 'パスキーでの確認がキャンセルされました'
                : 'パスキーで確認できませんでした');
        } finally {
            if (confirmPasskey) confirmPasskey.disabled = false;
        }
    };

    // ─── 最初の登録のメール確認 ───
    function stopResendTimer() {
        if (resendTimer) {
            clearInterval(resendTimer);
            resendTimer = null;
        }
        if (emailResend) {
            emailResend.disabled = false;
            emailResend.textContent = resendLabel;
        }
    }

    const startResendTimer = () => {
        stopResendTimer();
        if (!emailResend) return;
        let remaining = RESEND_WAIT_SECONDS;
        emailResend.disabled = true;
        emailResend.textContent = `${resendLabel}（${remaining}秒）`;
        resendTimer = setInterval(() => {
            remaining -= 1;
            if (remaining <= 0) {
                stopResendTimer();
                return;
            }
            emailResend.textContent = `${resendLabel}（${remaining}秒）`;
        }, 1000);
    };

    const sendEmailCode = async () => {
        setError(null);
        if (emailSentTo) emailSentTo.textContent = '';
        const response = await request(`${basePath}/email-code`, 'POST').catch(() => null);
        if (response && response.ok) {
            const { sent_to: sentTo } = await response.json();
            if (emailSentTo) emailSentTo.textContent = `${sentTo} に確認コードを送信しました`;
            startResendTimer();
            if (emailInput) emailInput.focus();
            return;
        }
        const code = await readErrorCode(response);
        setError(EMAIL_CODE_MESSAGES[code] ?? '確認コードを送信できませんでした');
    };

    const askEmailVerification = () => {
        const promise = waitVerification(emailArea);
        sendEmailCode();
        return promise;
    };

    const submitEmailCode = async () => {
        if (!pendingVerification || !emailInput) return;
        const code = emailInput.value.trim();
        if (code === '') {
            setError(EMAIL_CODE_MESSAGES.EMAIL_CODE_INVALID);
            emailInput.focus();
            return;
        }
        setError(null);
        if (emailSubmit) emailSubmit.disabled = true;
        try {
            const response = await request(`${basePath}/email-code/verify`, 'POST', { code }).catch(() => null);
            if (response && response.ok) {
                closeVerification(true);
                return;
            }
            const errorCode = await readErrorCode(response);
            setError(EMAIL_CODE_MESSAGES[errorCode] ?? '確認コードを確認できませんでした');
            emailInput.focus();
        } finally {
            if (emailSubmit) emailSubmit.disabled = false;
        }
    };

    /**
     * リクエストを送り、本人確認が必要なら確認後にもう一度送る。
     * キャンセルした場合は code = VERIFICATION_CANCELLED のエラーを投げる
     */
    const sendWithVerification = async (send) => {
        const response = await send();
        const required = await readVerificationRequired(response);
        if (!required) return response;
        const verified = required.error === 'EMAIL_VERIFICATION_REQUIRED'
            ? await askEmailVerification()
            : await askReauthentication(required.methods);
        if (!verified) {
            const error = new Error(VERIFICATION_CANCELLED);
            error.code = VERIFICATION_CANCELLED;
            throw error;
        }
        return send();
    };

    // フォーム内に置かれても Enter で外側のフォームを送信しない
    const onEnter = (input, handler) => {
        if (!input) return;
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                handler();
            }
        });
    };
    const onClick = (button, handler) => {
        if (!button) return;
        button.addEventListener('click', (e) => {
            e.preventDefault();
            handler();
        });
    };
    const cancelVerification = () => {
        setError(null);
        closeVerification(false);
    };
    onClick(confirmSubmit, submitPassword);
    onClick(confirmPasskey, submitPasskey);
    onClick(confirmCancel, cancelVerification);
    onEnter(confirmInput, submitPassword);
    onClick(emailSubmit, submitEmailCode);
    onClick(emailResend, sendEmailCode);
    onClick(emailCancel, cancelVerification);
    onEnter(emailInput, submitEmailCode);

    const createButton = (label, onClick, style = null) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'c-buttons-action';
        button.dataset.size = 's';
        if (style) button.dataset.style = style;
        button.textContent = label;
        button.addEventListener('click', onClick);
        return button;
    };

    /** 一覧の行を名前の編集状態にする（追加直後は $justAdded = true） */
    const startEdit = (item, passkey, justAdded = false) => {
        const text = item.querySelector('.c-passkey-manager__text');
        const actions = item.querySelector('.c-passkey-manager__actions');
        const nameArea = text.querySelector('.c-passkey-manager__name');

        const editArea = document.createElement('div');
        editArea.className = 'c-passkey-manager__edit';
        if (justAdded) {
            const lead = document.createElement('p');
            lead.className = 'c-passkey-manager__exp';
            lead.textContent = 'パスキーを追加しました。あとで見分けられるよう名前を付けてください（例: 仕事用MacBook）。';
            editArea.appendChild(lead);
        }
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'c-passkey-manager__input';
        input.maxLength = nameMaxLength;
        input.value = passkey.name;
        input.placeholder = '例: 仕事用MacBook';
        input.autocomplete = 'off';
        input.setAttribute('aria-label', 'パスキーの名前');

        const finish = () => {
            editArea.remove();
            nameArea.hidden = false;
            actions.hidden = false;
        };
        const save = async () => {
            const value = input.value.trim();
            if (value === '') {
                setError('パスキーの名前を入力してください');
                input.focus();
                return;
            }
            setError(null);
            saveButton.disabled = true;
            const response = await request(`${basePath}/${passkey.id}`, 'PATCH', { name: value }).catch(() => null);
            saveButton.disabled = false;
            if (!response || !response.ok) {
                setError(`パスキーの名前を変更できませんでした（${nameMaxLength}文字以内で入力してください）`);
                input.focus();
                return;
            }
            const data = await response.json();
            passkey.name = data.passkey.name;
            nameArea.textContent = passkey.name;
            finish();
        };
        const saveButton = createButton('保存', save);
        const cancelButton = createButton(justAdded ? 'あとで設定する' : 'キャンセル', () => {
            setError(null);
            finish();
        });
        input.addEventListener('keydown', (e) => {
            // フォーム内に置かれても Enter で外側のフォームを送信しない
            if (e.key === 'Enter') {
                e.preventDefault();
                save();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                finish();
            }
        });

        const row = document.createElement('div');
        row.className = 'c-passkey-manager__row';
        row.append(input, saveButton, cancelButton);
        editArea.appendChild(row);

        nameArea.hidden = true;
        actions.hidden = true;
        text.insertBefore(editArea, nameArea);
        input.focus();
        input.select();
    };

    const render = (passkeys, editId = null) => {
        if (!listArea) return;
        listArea.innerHTML = '';
        listArea.hidden = passkeys.length === 0;
        passkeys.forEach((passkey) => {
            const item = document.createElement('li');
            item.className = 'c-passkey-manager__item';

            const text = document.createElement('div');
            text.className = 'c-passkey-manager__text';
            const name = document.createElement('p');
            name.className = 'c-passkey-manager__name';
            name.textContent = passkey.name;
            const meta = document.createElement('p');
            meta.className = 'c-passkey-manager__meta';
            meta.textContent = `登録: ${formatDateTime(passkey.created_at)} / 最終利用: ${formatDateTime(passkey.last_used_at)}`;
            text.append(name, meta);

            const actions = document.createElement('div');
            actions.className = 'c-passkey-manager__actions';
            actions.append(
                createButton('名前を変更', () => startEdit(item, passkey)),
                createButton('削除', () => handleDelete(passkey), 'delete'),
            );

            item.append(text, actions);
            listArea.appendChild(item);
            if (editId !== null && passkey.id === editId) startEdit(item, passkey, true);
        });
    };

    const load = async (editId = null) => {
        const response = await request(basePath, 'GET').catch(() => null);
        if (response && response.ok) {
            const data = await response.json();
            render(data.passkeys || [], editId);
        }
    };

    const handleRegister = async () => {
        setError(null);
        addButton.disabled = true;
        addButton.textContent = '登録中...';
        try {
            const optionsResponse = await sendWithVerification(() => request(`${basePath}/options`, 'POST'));
            if (!optionsResponse.ok) throw new Error('パスキーの登録を開始できませんでした');
            const { options } = await optionsResponse.json();

            const response = await startRegistration({ optionsJSON: options });

            // 名前は登録後に付けてもらうため、ここでは送らない（既定名で登録される）
            const saveResponse = await sendWithVerification(() => request(basePath, 'POST', {
                response: JSON.stringify(response),
            }));
            if (!saveResponse.ok) throw new Error('パスキーを登録できませんでした');
            const { passkey } = await saveResponse.json();

            if (root.dataset.redirectOnRegister) {
                window.location.href = root.dataset.redirectOnRegister;
                return;
            }
            // 追加した行をそのまま名前の編集状態にする
            await load(passkey ? passkey.id : null);
        } catch (e) {
            // ユーザーが端末側のダイアログをキャンセルした場合もここに来る
            if (isVerificationCancelled(e)) {
                // 本人確認をキャンセルした場合は何も表示しない
            } else if (e && e.name === 'NotAllowedError') {
                setError('パスキーの登録がキャンセルされました');
            } else if (e instanceof Error && /^パスキー/.test(e.message)) {
                setError(e.message);
            } else {
                setError('パスキーを登録できませんでした');
            }
        } finally {
            addButton.disabled = false;
            addButton.textContent = addLabel;
        }
    };

    const handleDelete = async (passkey) => {
        if (!confirm(`パスキー「${passkey.name}」を削除します。\nよろしいですか？`)) return;
        setError(null);
        let response = null;
        try {
            response = await sendWithVerification(() => request(`${basePath}/${passkey.id}`, 'DELETE').catch(() => null));
        } catch (e) {
            if (isVerificationCancelled(e)) return;
        }
        if (!response || !response.ok) {
            setError('パスキーを削除できませんでした');
            return;
        }
        await load();
    };

    if (addButton) {
        addButton.addEventListener('click', (e) => {
            e.preventDefault();
            handleRegister();
        });
    }
    load();
};

const main = () => {
    document.querySelectorAll('.c-passkey-manager[data-base-path]').forEach(setupManager);

    // 運営によるパスキー削除・アカウント復旧の承認／却下フォームの確認ダイアログ
    document.querySelectorAll('form.js-confirm-passkey-delete, form.js-confirm-account-recovery').forEach((form) => {
        form.addEventListener('submit', (e) => {
            const message = form.dataset.confirm || 'パスキーを削除します。よろしいですか？';
            if (!confirm(message)) {
                e.preventDefault();
            }
        });
    });
};

export default main;
